import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { UploadCloud, AlertCircle, CheckCircle2, ImagePlus, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MissingArtworksManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const r = await fetch(`${BACKEND_URL}/api/admin/missing-artworks`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setItems(data.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="missing-artworks-page">
      <div className="mb-8">
        <h1 className="text-3xl font-light tracking-tight">Missing Artworks</h1>
        <p className="text-gray-500 mt-2 max-w-2xl">
          The reconciliation found {items.length} artwork
          {items.length === 1 ? '' : 's'} we couldn&rsquo;t auto-source.
          Drag-drop a photo, confirm the details, and we&rsquo;ll add it to
          the live shop.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 flex items-start gap-3" data-testid="missing-artworks-error">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-3 text-gray-500" data-testid="missing-artworks-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 rounded-lg bg-green-50 border border-green-200 flex items-center gap-3" data-testid="missing-artworks-empty">
          <CheckCircle2 className="w-6 h-6 text-green-700" />
          <div>
            <div className="font-medium text-green-900">All caught up</div>
            <div className="text-sm text-green-700">No unmatched Excel rows remaining.</div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {items.map((item) => (
            <MissingItemCard key={item.key} item={item} onImported={fetchItems} />
          ))}
        </div>
      )}
    </div>
  );
}

function MissingItemCard({ item, onImported }) {
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    title: item.title,
    artist: item.artist,
    year: item.year || '',
    medium: '',
    dimensions: '',
    edition: typeof item.edition === 'number' ? `Edition of ${item.edition}` : (item.edition || ''),
    description: '',
  });
  const fileInputRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      setError('Image larger than 12MB');
      return;
    }
    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      setError('Please attach an image first.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', imageFile);
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      const r = await fetch(
        `${BACKEND_URL}/api/admin/missing-artworks/${encodeURIComponent(item.key)}/import`,
        { method: 'POST', credentials: 'include', body: fd }
      );
      if (!r.ok) {
        const msg = await r.text();
        throw new Error(msg || `HTTP ${r.status}`);
      }
      setSuccess(true);
      setTimeout(() => onImported(), 1200);
    } catch (e2) {
      setError(e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-6 rounded-lg bg-green-50 border border-green-200 flex items-center gap-4"
        data-testid={`missing-success-${item.key}`}
      >
        <CheckCircle2 className="w-6 h-6 text-green-700" />
        <div>
          <div className="font-medium text-green-900">Added to shop</div>
          <div className="text-sm text-green-700">{form.artist} — {form.title}</div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.form
      initial={{ y: 8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      onSubmit={onSubmit}
      className="grid md:grid-cols-[260px_1fr] gap-6 p-5 bg-white rounded-lg border border-gray-200"
      data-testid={`missing-item-${item.key}`}
    >
      {/* Drop zone */}
      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="aspect-[4/5] rounded-md border-2 border-dashed border-gray-300 hover:border-black flex items-center justify-center cursor-pointer relative bg-gray-50 overflow-hidden transition"
        data-testid={`missing-dropzone-${item.key}`}
      >
        {previewUrl ? (
          <img src={previewUrl} alt="preview" className="w-full h-full object-cover" />
        ) : (
          <div className="text-center text-gray-500 text-sm px-4">
            <UploadCloud className="w-8 h-8 mx-auto mb-2" />
            <div>Drag a photo here or click to choose</div>
            <div className="text-xs text-gray-400 mt-1">JPG / PNG / WEBP · max 12MB</div>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`missing-file-input-${item.key}`}
        />
      </div>

      {/* Fields */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 className="text-lg font-medium">{item.artist}</h3>
          <span className="text-gray-400 text-sm">·</span>
          <span className="text-gray-600">{item.title}</span>
          {item.year && (
            <span className="text-xs px-2 py-0.5 bg-gray-100 rounded text-gray-600">
              {item.year}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid={`missing-title-${item.key}`} />
          <Field label="Year" value={form.year} onChange={(v) => setForm({ ...form, year: v })} testid={`missing-year-${item.key}`} />
          <Field label="Medium" placeholder="e.g. Screenprint on paper" value={form.medium} onChange={(v) => setForm({ ...form, medium: v })} testid={`missing-medium-${item.key}`} />
          <Field label="Dimensions" placeholder="e.g. 75 x 56 cm" value={form.dimensions} onChange={(v) => setForm({ ...form, dimensions: v })} testid={`missing-dimensions-${item.key}`} />
          <Field label="Edition" placeholder="e.g. Edition of 125" value={form.edition} onChange={(v) => setForm({ ...form, edition: v })} testid={`missing-edition-${item.key}`} />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">Description (optional)</label>
          <textarea
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full text-sm px-3 py-2 border border-gray-200 rounded focus:outline-none focus:border-black"
            data-testid={`missing-description-${item.key}`}
          />
        </div>

        {error && (
          <div className="text-sm text-red-600 flex items-center gap-2" data-testid={`missing-error-${item.key}`}>
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="submit"
            disabled={submitting || !imageFile}
            className="px-5 py-2 bg-black text-white rounded hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 transition"
            data-testid={`missing-submit-${item.key}`}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
            Add to shop
          </button>
        </div>
      </div>
    </motion.form>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full text-sm px-3 py-2 border border-gray-200 rounded focus:outline-none focus:border-black"
        data-testid={testid}
      />
    </div>
  );
}
